import React, { useState } from 'react';
import { useLocation } from 'react-router-dom';
import '../styles/questionmock.css';
import {useNavigate} from 'react-router-dom';
import usePreventBack from "../hooks/usePreventBack";


const roleOptions = [
  { id: 'frontend', label: 'Frontend Developer', desc: 'HTML, CSS, JS, UI logic', icon: '🎨' },
  { id: 'react', label: 'React Developer', desc: 'Hooks, state, components', icon: '⚛️' },
  { id: 'node', label: 'Node.js Developer', desc: 'APIs, servers, async JS', icon: '🟢' },
  { id: 'java', label: 'Java Developer', desc: 'OOP, Spring, collections', icon: '☕' },
  { id: 'data', label: 'Data Analyst', desc: 'SQL, Excel, visualization', icon: '📊' },
  { id: 'ai', label: 'AI Engineer', desc: 'ML, models, pipelines', icon: '🤖' },
  { id: 'hr', label: 'HR Interview', desc: 'Behavioral & culture-fit', icon: '🧑‍💼' },
  { id: 'custom', label: 'Custom', desc: 'Type your own role', icon: '✏️' },
];

const experienceOptions = [
  { id: 'fresher', label: 'Fresher', desc: '0 years · just starting out', icon: '🌱' },
  { id: '1-2', label: '1-2 Years', desc: 'Early career', icon: '🚶' },
  { id: '3-5', label: '3-5 Years', desc: 'Mid-level', icon: '🏃' },
  { id: 'senior', label: 'Senior', desc: '5+ years · leadership-ready', icon: '🏆' },
];

// New: Step 3 — optional resume upload. This replaces the older
// language/duration selection step that used to sit here.
const steps = [
  { key: 'role', title: 'Select Role', subtitle: 'What role are you preparing for?', eyebrow: 'Step 1', type: 'options', options: roleOptions },
  { key: 'experience', title: 'Experience Level', subtitle: 'This helps us calibrate question depth.', eyebrow: 'Step 2', type: 'options', options: experienceOptions },
  { key: 'resume', title: 'Upload Resume', subtitle: 'Optional — helps us tailor questions to your background.', eyebrow: 'Step 3', type: 'upload' },
];

// Fixed defaults now that language and duration selection have been removed.
const DEFAULT_LANGUAGE = 'english';
const DEFAULT_DURATION = '20';
const questionCount = { '10': 5, '20': 9, '30': 14 };

const MAX_RESUME_SIZE_KB = 100;
const ACCEPTED_RESUME_TYPES = '.pdf,.doc,.docx';

const START_INTERVIEW_URL = 'https://codematrix.co.in/auth/api/users/api/mock-interview/start';

// Maps our experience option ids to a rough numeric "years" value the API expects.
const experienceYearsMap = {
  fresher: '0',
  '1-2': '1',
  '3-5': '3',
  senior: '5',
};

// Simple unique-ish session id generator (timestamp + random suffix).
const generateSessionId = () =>
  `interview-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;

/* ---------------- Component ---------------- */

export default function MockInterviewSetup() {
  usePreventBack(); 
  const location = useLocation();
  const navigate = useNavigate();
  const [stepIndex, setStepIndex] = useState(0); // -1 reserved for none; 0..n = steps, n+1 = summary
  const [answers, setAnswers] = useState({ duration: DEFAULT_DURATION, language: DEFAULT_LANGUAGE });
  const [customRole, setCustomRole] = useState('');
  const [phase, setPhase] = useState('setup'); // setup | loading | ready

  const [resumeFile, setResumeFile] = useState(null); // File object
  const [resumeError, setResumeError] = useState('');
  const [startError, setStartError] = useState('');

  const onSummary = stepIndex === steps.length;
  const current = !onSummary ? steps[stepIndex] : null;
  const currentValue = current ? answers[current.key] : null;

  const isStepAnswered = (key) => {
    if (key === 'role') {
      return answers.role && (answers.role !== 'custom' || customRole.trim().length > 0);
    }
    if (key === 'resume') {
      return true;
    }
    return Boolean(answers[key]);
  };

  const canAdvance = current ? isStepAnswered(current.key) : false;
  const progressPct = ((stepIndex + (canAdvance || onSummary ? 1 : 0)) / (steps.length + 1)) * 100;

  const select = (key, value) => {
    setAnswers((prev) => ({ ...prev, [key]: value }));
  };

  const handleResumeChange = (e) => {
  const file = e.target.files && e.target.files[0];
  if (!file) return;

  const sizeKb = file.size / 1024;
  if (sizeKb > MAX_RESUME_SIZE_KB) {
    setResumeError(`File is too large. Max size is ${MAX_RESUME_SIZE_KB}KB.`);
    return;
  }

  setResumeError('');
  setResumeFile(file);
};

  const removeResume = () => {
    setResumeFile(null);
    setResumeError('');
  };

  const goNext = () => {
    if (onSummary) return;
    if (!canAdvance) return;
    setStepIndex((s) => s + 1);
  };

  const goBack = () => {
    if (stepIndex > 0) setStepIndex((s) => s - 1);
  };

  const jumpTo = (idx) => {
    const key = steps[idx].key;
    if (isStepAnswered(key) || idx < stepIndex) setStepIndex(idx);
  };

  const roleLabel =
    answers.role === 'custom'
      ? customRole.trim() || 'Custom role'
      : roleOptions.find((r) => r.id === answers.role)?.label;


  const startInterview = async () => {
    setStartError('');
    setPhase('loading');

    const sessionId = generateSessionId();
    localStorage.setItem("mockInterviewSessionId", sessionId);
    const experienceYears = experienceYearsMap[answers.experience] ?? '0';

    const formData = new FormData();
    formData.append('sessionId', sessionId);
    formData.append('name', localStorage.getItem("username") ); // TODO: replace with the logged-in user's actual name
    formData.append('role', roleLabel || '');
    formData.append('experience', experienceYears);
    if (resumeFile) {
      formData.append('resume', resumeFile);
    }

    try {
      const token = localStorage.getItem("token");
      const response = await fetch(START_INTERVIEW_URL, {
        method: 'POST',
         headers: {
    Authorization: `${token}`,
  },
        body: formData,
      });

      if (!response.ok) {
        throw new Error(`Request failed with status ${response.status}`);
      }

      const data = await response.text();
      console.log(sessionId)

navigate("/mockinterview/loading",
   {
  state: { message: data }
}
);
    } catch (err) {
      console.error('Failed to start interview:', err);
      setPhase('setup');
      setStartError('Something went wrong while starting the interview. Please try again.');
    }
  };

  const experienceLabel = experienceOptions.find((o) => o.id === answers.experience)?.label;
  const qCount = questionCount[answers.duration] || '-';

  const summaryRows = [
    { label: 'Role', value: roleLabel, stepIdx: 0 },
    { label: 'Experience', value: experienceLabel, stepIdx: 1 },
    { label: 'Resume', value: resumeFile ? resumeFile.name : 'Not provided', stepIdx: 2 },
  ];

  /* ---------------- Loading screen ---------------- */
  if (phase === 'loading') {
    return (
      <div className="mi-page">
        <div className="mi-loading-container">
          <div className="mi-spinner" />
          <h1 className="mi-loading-title">Preparing your interview</h1>
          <p className="mi-loading-text">
            Generating {qCount} questions for {roleLabel}...
          </p>
        </div>
      </div>
    );
  }



  /* ---------------- Summary screen ---------------- */
  if (onSummary) {
    return (
      <div className="mi-page">
        <div className="mi-container">
          <div className="mi-progress-row">
            <span className="mi-progress-label">Review</span>
            <span className="mi-progress-label">{Math.round(progressPct)}%</span>
          </div>
          <div className="mi-progress-track">
            <div className="mi-progress-fill" style={{ width: `${progressPct}%` }} />
          </div>

          <div className="mi-card">
            <div className="mi-header">
              <p className="mi-eyebrow">Final check</p>
              <h1 className="mi-title">Review your setup</h1>
              <p className="mi-subtitle">Make sure everything looks right before you begin.</p>
            </div>

            <div className="mi-summary-list">
              {summaryRows.map((row) => (
                <div className="mi-summary-row" key={row.label}>
                  <div>
                    <div className="mi-summary-label">{row.label}</div>
                    <div className="mi-summary-value">{row.value}</div>
                  </div>
                  <button className="mi-summary-edit" onClick={() => setStepIndex(row.stepIdx)}>
                    Edit
                  </button>
                </div>
              ))}
            </div>



            <div className="mi-tip">
              <span className="mi-tip-icon">💡</span>
              <span className="mi-tip-text">
                Find a quiet spot and check your mic before you start — answers are evaluated as you speak,
                and you won't be able to pause once the timer begins.
              </span>
            </div>

            {startError && <p className="mi-resume-error">{startError}</p>}

            <button className="mi-start-btn" onClick={startInterview}>
              Next Step →
            </button>
            <button className="mi-back-link" onClick={() => setStepIndex(steps.length - 1)}>
              ← Back to Edit
            </button>
          </div>
        </div>
      </div>
    );
  }

  /* ---------------- Step screen (role / experience / resume) ---------------- */
  return (
    <div className="mi-page">
      <div className="mi-container">
        <div className="mi-brand-row">
          <span className="mi-brand">
            <span className="mi-brand-dot" />
            Mock Interview Setup
          </span>
        </div>

        <div className="mi-progress-row">
          <span className="mi-progress-label">Step {stepIndex + 1} of {steps.length + 1}</span>
          <span className="mi-progress-label">{Math.round(progressPct)}%</span>
        </div>
        <div className="mi-progress-track">
          <div className="mi-progress-fill" style={{ width: `${progressPct}%` }} />
        </div>

        <div className="mi-card">
          <div className="mi-header">
            <p className="mi-eyebrow">{current.eyebrow}</p>
            <h1 className="mi-title">{current.title}</h1>
            <p className="mi-subtitle">{current.subtitle}</p>
          </div>

          <div className="mi-dots">
            {steps.map((s, idx) => {
              const answered = isStepAnswered(s.key);
              const clickable = answered || idx < stepIndex;
              return (
                <button
                  key={s.key}
                  onClick={() => jumpTo(idx)}
                  disabled={!clickable}
                  title={s.title}
                  className={`mi-dot ${idx === stepIndex || answered ? 'mi-dot--filled' : ''} ${
                    clickable ? 'mi-dot--clickable' : 'mi-dot--disabled'
                  }`}
                />
              );
            })}
          </div>

          {current.type === 'options' && (
            <div className="mi-options">
              {current.options.map((opt) => {
                const isSelected = currentValue === opt.id;
                return (
                  <button
                    key={opt.id}
                    className={`mi-option ${isSelected ? 'mi-option--selected' : ''}`}
                    onClick={() => select(current.key, opt.id)}
                  >
                    <span className="mi-option-icon">{opt.icon}</span>
                    <span className="mi-option-text">
                      <div className="mi-option-label">{opt.label}</div>
                      <div className="mi-option-desc">{opt.desc}</div>
                    </span>
                    <span className="mi-radio">
                      <span className="mi-radio-fill" />
                    </span>
                  </button>
                );
              })}
            </div>
          )}

          {current.key === 'role' && answers.role === 'custom' && (
            <input
              type="text"
              className="mi-custom-input"
              placeholder="e.g. DevOps Engineer, QA Tester..."
              value={customRole}
              onChange={(e) => setCustomRole(e.target.value)}
              autoFocus
            />
          )}

         {current.type === "upload" && (
  <div className="mi-resume-upload">
    {!resumeFile ? (
      <label className="mi-resume-dropzone">
        <input
          type="file"
          accept="application/pdf,.pdf"
          onChange={handleResumeChange}
          className="mi-resume-input"
        />

        <span className="mi-resume-icon">📄</span>
        <span className="mi-resume-label">
          Click to upload your resume
        </span>
        <span className="mi-resume-hint">
          PDF only · up to {MAX_RESUME_SIZE_KB}KB · optional
        </span>
      </label>
    ) : (
      <div className="mi-resume-file">
        <span className="mi-resume-icon">📄</span>
        <span className="mi-resume-filename">
          {resumeFile.name}
        </span>

        <button
          className="mi-resume-remove"
          onClick={removeResume}
          type="button"
        >
          Remove
        </button>
      </div>
    )}

    {resumeError && (
      <p className="mi-resume-error">
        {resumeError}
      </p>
    )}

    <p className="mi-resume-skip-hint">
      You can skip this step and continue without a resume.
    </p>
  </div>
)}

          <div className="mi-nav">
            <button className="mi-btn mi-btn--back" onClick={goBack} disabled={stepIndex === 0}>
              <svg className="mi-chevron" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <polyline points="15 18 9 12 15 6" />
              </svg>
              Back
            </button>
            <button className="mi-btn mi-btn--next" onClick={goNext} disabled={!canAdvance}>
              {current.key === 'resume' && !resumeFile
                ? 'Skip'
                : stepIndex === steps.length - 1
                ? 'Review'
                : 'Next'}
              <svg className="mi-chevron" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <polyline points="9 18 15 12 9 6" />
              </svg>
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}